import { useEffect, useCallback } from 'react'
import { Box, Flex, Image, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import IconClose from '@/components/icons/IconClose'
import { downloadFromUrl } from '@/utils/download'
import DesignMetadataPanel from './design-metadata-panel'
import ProPluginsPanel from './pro-plugins-section'
import '@/components/ambient/animations.css'

import type { Design } from '@/api/designs'

interface LightboxProps {
  design: Design
  onClose: () => void
}

export default function Lightbox({ design, onClose }: LightboxProps) {
  const imageUrl = design.imageUrl ?? ''

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [handleKeyDown])

  const handleDownload = () => {
    if (!imageUrl) return
    void downloadFromUrl(imageUrl, `ablo-design-${Date.now()}`)
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(8, 10, 14, 0.9)',
          backdropFilter: 'blur(8px)',
          overflow: 'auto'
        }}
        onClick={onClose}
      >
        {/* Close button */}
        <Box
          position="fixed"
          top="20px"
          right="20px"
          w="34px"
          h="34px"
          borderRadius="full"
          bg="rgba(20, 23, 30, 0.8)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="all 0.2s ease"
          _hover={{ bg: 'rgba(34, 39, 50, 0.85)' }}
          onClick={onClose}
          zIndex={1001}
        >
          <IconClose color="text.primary" />
        </Box>

        {/* Download button */}
        <Box
          position="fixed"
          top="20px"
          right="64px"
          h="34px"
          px="12px"
          borderRadius="full"
          bg="rgba(20, 23, 30, 0.8)"
          border="1px solid"
          borderColor="rgba(255,255,255,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="all 0.2s ease"
          _hover={{ bg: 'rgba(34, 39, 50, 0.85)' }}
          onClick={(e) => {
            e.stopPropagation()
            handleDownload()
          }}
          zIndex={1001}
        >
          <Text color="whiteAlpha.800" textStyle="bodySuperSmall">
            Download
          </Text>
        </Box>

        {/* Content: Image + Metadata */}
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={{ base: '14px', lg: '24px' }}
            alignItems={{ base: 'center', lg: 'flex-start' }}
            maxW="97vw"
            maxH={{ base: 'none', lg: '85vh' }}
          >
            <Image
              src={imageUrl}
              alt={design.name}
              maxW={{ base: '90vw', lg: '65vw' }}
              maxH={{ base: '60vh', lg: '85vh' }}
              objectFit="contain"
              borderRadius="10px"
              border="1px solid"
              borderColor="rgba(255,255,255,0.08)"
              flexShrink={0}
            />

            <Box
              w={{ base: '90vw', lg: '300px', xl: '340px', '2xl': '360px' }}
              maxH={{ base: 'none', lg: '85vh' }}
              overflowY="auto"
              flexShrink={0}
              py={{ base: '0', lg: '2px' }}
              css={{
                '&::-webkit-scrollbar': { width: '3px' },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(255,255,255,0.14)',
                  borderRadius: '4px'
                }
              }}
            >
              <DesignMetadataPanel design={design} />

              {/* Integrations upsell */}
              <Box
                mt="14px"
                pt="14px"
                borderTop="1px solid"
                borderColor="rgba(255,255,255,0.06)"
              >
                <ProPluginsPanel />
              </Box>
            </Box>
          </Flex>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
